import cx from "classnames";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QesActiveFiltersBar } from "@app/shared/QesActiveFiltersBar";
import type { FilterChipDescriptor } from "@app/shared/QesActiveFiltersBar";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { Accordion } from "@app/shared/Accordion";
import { Toggle } from "@app/shared/Toggle";
import DualRangeSlider from "@app/shared/DualRangeSlider";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { ProvidersFilterOptionsType } from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import {
  ConvertProviderSourceFilterOptionsToDisplay,
} from "@app/projects/network-assessment/maps/network-coverage-map/DisplayConverters";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { ZipProviderFilters } from "@app/projects/zip-projects/maps/interfaces";
import {
  eligibleFilterChildren,
  providerSourceFiltersComparer,
} from "@app/projects/zip-projects/maps/zipProviderFilters";
import {
  HUNDRED_PERCENT_FILTER,
  ZERO_PERCENT_FILTER,
} from "@app/projects/zip-projects/maps/ZipCoverageMapConstants";

interface ZipCoverageMapFloatingButtonsProps {
  appliedRegionLabel: string;
  appliedSpecialtyLabel?: string;
  isLeftPanelOpen: boolean;
  onUpdateInputs: () => void;
  providerFilterOptions: ProvidersFilterOptionsType[];
  appliedFilters: ZipProviderFilters;
  onApplyFilters: (filters: ZipProviderFilters) => void;
  hasExcludedProviders: boolean;
  loadingProviders: boolean;
}

/**
 * Floating map controls (after Create): Update Inputs + the Filters flyout  over the map's
 * top-left. The flyout narrows the provider pins by Provider Source and Action Status and by Provider
 * Contribution Measures; member points and the Map Details metrics are untouched. Provider Claims
 * Insights is WI 201348 and slots in as a third accordion. Styling: _zip-coverage-map.scss.
 *
 * SOURCE (re-created, 199592 / ported, 202326): controls/ProviderAccordionControl.tsx (Update Inputs) +
 * controls/NetworkCoverageMapFloatingFilterControl.tsx (Filters — draft/cancel/apply/reset, the
 * click-outside handler and the parent tri-state maths). The predicate and the option tree live in
 * zipProviderFilters.ts because ZIP has no map context to hang them on. ZIP-owned.
 * CONSOLIDATION (199592): shared prop-driven `FloatingMapButton`; Filters → shared panel if county's is
 * refactored to props. No shared/generic context.
 */
export const ZipCoverageMapFloatingButtons: FC<ZipCoverageMapFloatingButtonsProps> = ({
  appliedRegionLabel,
  appliedSpecialtyLabel,
  isLeftPanelOpen,
  onUpdateInputs,
  providerFilterOptions,
  appliedFilters,
  onApplyFilters,
  hasExcludedProviders,
  loadingProviders,
}) => {
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);

  const [pendingOptions, setPendingOptions] = useState<ProvidersFilterOptions[]>(appliedFilters.providerFilterOptions);
  const [pendingShowExcluded, setPendingShowExcluded] = useState<boolean>(appliedFilters.showExcludedProviders);
  const [pendingServesRange, setPendingServesRange] = useState<[number, number]>(appliedFilters.servesRange);
  const [pendingGainsRange, setPendingGainsRange] = useState<[number, number]>(appliedFilters.gainsRange);
  const [pendingImpactRange, setPendingImpactRange] = useState<[number, number]>(appliedFilters.impactRange);

  const filterMenuRef = useRef<HTMLDivElement>(null);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);

  const cancelFilters = useCallback((): void => {
    setPendingOptions(appliedFilters.providerFilterOptions);
    setPendingShowExcluded(appliedFilters.showExcludedProviders);
    setPendingServesRange(appliedFilters.servesRange);
    setPendingGainsRange(appliedFilters.gainsRange);
    setPendingImpactRange(appliedFilters.impactRange);
  }, [appliedFilters]);

  useEffect(() => {
    cancelFilters();
  }, [cancelFilters]);

  const applyFilters = (): void => {
    onApplyFilters({
      gainsRange: pendingGainsRange,
      impactRange: pendingImpactRange,
      providerFilterOptions: pendingOptions,
      servesRange: pendingServesRange,
      showExcludedProviders: pendingShowExcluded,
    });
    setIsFilterMenuOpen(false);
  };

  const resetFilters = (): void => {
    setPendingOptions([]);
    setPendingShowExcluded(false);
    setPendingServesRange([ZERO_PERCENT_FILTER, HUNDRED_PERCENT_FILTER]);
    setPendingGainsRange([ZERO_PERCENT_FILTER, HUNDRED_PERCENT_FILTER]);
    setPendingImpactRange([ZERO_PERCENT_FILTER, HUNDRED_PERCENT_FILTER]);
  };

  const closeMenu = (): void => {
    setIsFilterMenuOpen(false);
    cancelFilters();
  };

  useEffect(() => {
    const onClickOutside = (event: MouseEvent): void => {
      if (
        !!filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target as Node) &&
        !filtersButtonRef.current?.contains(event.target as Node) &&
        isFilterMenuOpen) 
      {
        closeMenu();
      }
    };

    const onEscapeButtonPress = (event: KeyboardEvent): void => {
      if(isFilterMenuOpen && event.key === "Escape")
        closeMenu();
    }

    document.addEventListener("pointerdown", onClickOutside);
    document.addEventListener("keydown", onEscapeButtonPress);
    return () => {
      document.removeEventListener("pointerdown", onClickOutside);
      document.removeEventListener("keydown", onEscapeButtonPress);
    }
  }, [isFilterMenuOpen, cancelFilters]);

  const toggleOption = (option: ProvidersFilterOptions): void => {
    setPendingOptions((prev) => (prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]));
  };

  const toggleParent = (option: ProvidersFilterOptionsType): void => {
    const eligible = eligibleFilterChildren(option, pendingShowExcluded);
    const allChildTypes = (option.children ?? []).map((child) => child.type);
    const allEligibleSelected = eligible.length > 0 && eligible.every((child) => pendingOptions.includes(child.type));

    setPendingOptions((prev) =>
      allEligibleSelected
        ? prev.filter((o) => !allChildTypes.includes(o))
        : [...prev, ...eligible.map((child) => child.type).filter((type) => !prev.includes(type))]);
  };

  const onShowExcludedChange = (): void => {
    const nextShowExcluded = !pendingShowExcluded;
    setPendingShowExcluded(nextShowExcluded);

    if (!nextShowExcluded) {
      setPendingOptions((prev) =>
        prev.filter(
          (o) => o !== ProvidersFilterOptions.ExcludedMarket && o !== ProvidersFilterOptions.ExcludedProspect,
        ));
    }
  };

  const sourceChips = useMemo<FilterChipDescriptor[]>(() => {
    const chips: FilterChipDescriptor[] = [...pendingOptions]
      .sort(providerSourceFiltersComparer)
      .map((option) => ({
        content: ConvertProviderSourceFilterOptionsToDisplay[option],
        key: String(option),
        onDismiss: () => setPendingOptions((prev) => prev.filter((o) => o !== option)),
      }));

    return chips;
  }, [pendingOptions]);

  const measureChips = useMemo<FilterChipDescriptor[]>(() => {
    const chips: FilterChipDescriptor[] = [];
    const ranges: { key: string; label: string; range: [number, number]; reset: () => void }[] = [
      { key: "serves", label: "Serves", range: pendingServesRange, reset: () => setPendingServesRange([ZERO_PERCENT_FILTER, HUNDRED_PERCENT_FILTER]) },
      { key: "gains", label: "Gains", range: pendingGainsRange, reset: () => setPendingGainsRange([ZERO_PERCENT_FILTER, HUNDRED_PERCENT_FILTER]) },
      { key: "impact", label: "Impact", range: pendingImpactRange, reset: () => setPendingImpactRange([ZERO_PERCENT_FILTER, HUNDRED_PERCENT_FILTER]) },
    ];

    for (const { key, label, range, reset } of ranges) {
      const [min, max] = range;
      if (min !== ZERO_PERCENT_FILTER || max !== HUNDRED_PERCENT_FILTER) {
        chips.push({ content: `${label} %: ${min}% - ${max}%`, key, onDismiss: reset });
      }
    }

    return chips;
  }, [pendingServesRange, pendingGainsRange, pendingImpactRange]);

  const filterChips = useMemo<FilterChipDescriptor[]>(
    () => [...sourceChips, ...measureChips],
    [sourceChips, measureChips],
  );

  const contextChips = useMemo<FilterChipDescriptor[]>(() => {
    const chips: FilterChipDescriptor[] = [{ content: `Service Area: ${appliedRegionLabel}`, key: "service-area" }];
    if (appliedSpecialtyLabel) {
      chips.push({ content: `Specialty: ${appliedSpecialtyLabel}`, key: "specialty" });
    }
    return chips;
  }, [appliedRegionLabel, appliedSpecialtyLabel]);

  return (
    <div
      className={cx("zip-coverage-map__floating-controls", {
        "zip-coverage-map__floating-controls--offset": isLeftPanelOpen,
      })}
      data-html2canvas-ignore="true"
    >
      <div className="zip-coverage-map__floating-buttons">
        <QESButton
          className="zip-coverage-map__pill-button"
          qesType="none"
          onClick={onUpdateInputs}
          qesTestId="zip-coverage-map__update-inputs"
        >
          Update Inputs
        </QESButton>
        <QESButton
          className="zip-coverage-map__pill-button"
          qesType="none"
          disabled={loadingProviders}
          onClick={() => {
            cancelFilters();
            setIsFilterMenuOpen((prev) => !prev);
          }}
          qesTestId="zip-coverage-map__filters-button"
          qesRef={filtersButtonRef}
        >
          <QesIcon iconName={QesIcons.FilterLines} width="20px" height="20px" isDecorative />
          <span>Filters</span>
          <span className="zip-coverage-map__filter-counter">{filterChips.length}</span>
          <TooltipWrapper
            text="The map will update to reflect your selected filters. Provider points only — member points and the map metrics do not change."
            position="right"
            tooltipWidth="20rem"
          >
            <QesIcon iconName={QesIcons.InfoCircle} height="20px" width="20px" description="hover or focus for a tooltip" />
          </TooltipWrapper>
        </QESButton>
      </div>

      {isFilterMenuOpen &&
        <div className="floating-filters-menu-container" ref={filterMenuRef}>
          <div className="floating-filters-menu-header">
            <h4 className="header-s mb-0">Filters</h4>
            <div className="floating-filter-menu-header-right-container">
              <QESButton
                qesTestId="zip-coverage-map__floating-filters-menu-reset-filters-button"
                className="qes-button-secondary floating-filters-menu-reset-filters-button"
                qesType="none"
                onClick={resetFilters}
              >
                Reset Filters
              </QESButton>
              <QESButton
                qesTestId="zip-coverage-map__floating-filters-menu-close-button"
                className="qes-secondary floating-filters-menu-close-button"
                qesType="none"
                onClick={closeMenu}
                aria-label="Close filters"
              >
                <QesIcon iconName={QesIcons.XClose} width="24px" height="24px" color="#32323C" isDecorative/>
              </QESButton>
            </div>
          </div>
          <div className="floating-filters-menu-separator" />
          <QesActiveFiltersBar
            className="floating-filters-applied-filters-container"
            chips={[...contextChips, ...filterChips]}
            testIdPrefix="zip-coverage-map__floating-filters-applied-filter"
            variant="minimal"
          />
          <AccordionContainer className="floating-filters-menu-accordion-container">
            <Accordion
              title={
                <>
                  Provider Source and Action Status
                  <span className="floating-filters-circle-counter ms-2">{sourceChips.length}</span>
                </>
              }
              defaultOpen={true}
              testId="zip-coverage-map__floating-filters-provider-source-accordion"
            >
              <>
                {hasExcludedProviders &&
                  <div className="float-end">
                    <Toggle
                      active={pendingShowExcluded}
                      testId="zip-coverage-map__floating-filters-menu-show-excluded-providers"
                      label="Show Excluded Providers"
                      labelSide="right"
                      size="small"
                      onChange={onShowExcludedChange}
                    />
                  </div>
                }
                {providerFilterOptions.map((option) => {
                  const eligible = eligibleFilterChildren(option, pendingShowExcluded);
                  if (eligible.length === 0) {
                    return null;
                  }

                  const selectedCount = eligible.filter((child) => pendingOptions.includes(child.type)).length;

                  return (
                    <div key={option.type}>
                      <QESCheckbox
                        label={option.name}
                        onChange={() => toggleParent(option)}
                        checked={selectedCount === eligible.length}
                        indeterminate={selectedCount > 0 && selectedCount < eligible.length}
                        testId={option.id}
                        containerClassName="col-12"
                      />
                      <div className="ms-3">
                        {eligible.map((child) => (
                          <QESCheckbox
                            key={child.type}
                            label={child.name}
                            onChange={() => toggleOption(child.type)}
                            checked={pendingOptions.includes(child.type)}
                            testId={child.id}
                            containerClassName="col-12"
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            </Accordion>
            <Accordion
              title={
                <>
                  Provider Contribution Measures
                  <span className="floating-filters-circle-counter ms-2">{measureChips.length}</span>
                </>
              }
              defaultOpen={false}
              testId="zip-coverage-map__floating-filters-provider-metrics-accordion"
            >
              <>
                <DualRangeSlider
                  testId="zip-coverage-map__filter--serves-percent-slider"
                  label={
                    <>
                      Serves %
                      <TooltipWrapper
                        text="Applies to both In and Out-of-Network provider sources"
                        position="right"
                        containerClassName="ms-1"
                      >
                        <QesIcon iconName={QesIcons.InfoCircle} description="hover or focus for a tooltip" />
                      </TooltipWrapper>
                    </>
                  }
                  minValue={ZERO_PERCENT_FILTER}
                  maxValue={HUNDRED_PERCENT_FILTER}
                  onChange={setPendingServesRange}
                  value={pendingServesRange}
                />
                <div className="floating-filters-menu-separator mb-2 mt-2" />
                <DualRangeSlider
                  testId="zip-coverage-map__filter--gains-percent-slider"
                  label={
                    <>
                      Gains %
                      <TooltipWrapper
                        text="Only applies to Out-of-Network provider sources"
                        position="right"
                        containerClassName="ms-1"
                      >
                        <QesIcon iconName={QesIcons.InfoCircle} description="hover or focus for a tooltip" />
                      </TooltipWrapper>
                    </>
                  }
                  minValue={ZERO_PERCENT_FILTER}
                  maxValue={HUNDRED_PERCENT_FILTER}
                  onChange={setPendingGainsRange}
                  value={pendingGainsRange}
                />
                <div className="floating-filters-menu-separator mb-2 mt-2" />
                <DualRangeSlider
                  testId="zip-coverage-map__filter--impact-percent-slider"
                  label={
                    <>
                      Impact %
                      <TooltipWrapper
                        text="Only applies to Out-of-Network provider sources"
                        position="right"
                        containerClassName="ms-1"
                      >
                        <QesIcon iconName={QesIcons.InfoCircle} description="hover or focus for a tooltip" />
                      </TooltipWrapper>
                    </>
                  }
                  minValue={ZERO_PERCENT_FILTER}
                  maxValue={HUNDRED_PERCENT_FILTER}
                  onChange={setPendingImpactRange}
                  value={pendingImpactRange}
                />
              </>
            </Accordion>
          </AccordionContainer>
          <div className="floating-filters-apply-container floating-filters-menu-content">
            <QESButton
              qesTestId="zip-coverage-map__floating-filters-menu-apply-button"
              className=""
              qesType="primary"
              onClick={applyFilters}
            >
              Apply Filters
            </QESButton>
          </div>
        </div>
      }
    </div>
  );
};

export default ZipCoverageMapFloatingButtons;
